/**
 * O que faz: funções puras da revisão dos agentes (chave do item, data e frase da sugestão).
 * Onde usa: ListaRevisaoAgentes, ModalRevisaoAgentes e o card "Para revisar" da aba Agentes.
 * Conexões: tipo ItemRevisao de contratos.ts.
 * Tabelas: nenhuma.
 * Data: 26/09/2026.
 */
import type { ItemRevisao } from './contratos';

/** Chave estável do item: retorno tem id próprio; sugestão de etapa é uma por lead. */
export function chaveItemRevisao(item: ItemRevisao): string {
  return `${item.tipo}-${item.itemId ?? item.leadId}`;
}

export function formatarDataRevisao(iso: string | null): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    .format(new Date(iso));
}

/** Frase curta da sugestão, igual na lista e na barra de decisão do modal. */
export function resumoSugestao(item: ItemRevisao): string {
  const confianca = item.confianca != null ? ` · confiança ${Math.round(item.confianca * 100)}%` : '';
  if (item.tipo === 'retorno') {
    return `Retomar em ${formatarDataRevisao(item.agendarPara)}${item.dataEstimada ? ' (data estimada)' : ''}${confianca}`;
  }
  return `${item.etapaAtual ?? 'Sem coluna'} → ${item.etapaSugerida ?? 'coluna sugerida'}${confianca}`;
}

/**
 * Próximo item depois de uma decisão: o seguinte da lista, ou o anterior se era o último.
 * Motivo: a pessoa revisa em sequência sem voltar para a lista a cada decisão.
 */
export function proximoItemRevisao(itens: ItemRevisao[], atual: ItemRevisao): ItemRevisao | null {
  const indice = itens.findIndex((item) => chaveItemRevisao(item) === chaveItemRevisao(atual));
  if (indice === -1) return itens[0] ?? null;
  return itens[indice + 1] ?? itens[indice - 1] ?? null;
}

// ── Data do retorno editável na revisão ──────────────────────────────────────
// Brasília não tem horário de verão desde 2019: o deslocamento fixo -03:00 evita depender do fuso do navegador.
const DESLOCAMENTO_BRASILIA = '-03:00';
const LIMITE_DIAS_RETORNO = 365;

/** ISO → campos de data e hora como a equipe vê em Brasília (para preencher os inputs). */
export function camposDataRetorno(iso: string | null): { data: string; hora: string } {
  if (!iso) return { data: '', hora: '10:00' };
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const valor = (tipo: string) => partes.find((parte) => parte.type === tipo)?.value ?? '';
  return { data: `${valor('year')}-${valor('month')}-${valor('day')}`, hora: `${valor('hour')}:${valor('minute')}` };
}

/** Campos da tela → ISO com o fuso de Brasília; null quando a data está incompleta. */
export function isoDataRetorno(data: string, hora: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(data) || !/^\d{2}:\d{2}$/.test(hora)) return null;
  const instante = new Date(`${data}T${hora}:00${DESLOCAMENTO_BRASILIA}`);
  return Number.isNaN(instante.getTime()) ? null : instante.toISOString();
}

/** Atalhos da tela ("1 semana", "1 mês"...): soma dias a hoje, no calendário de Brasília. */
export function dataDaquiADias(dias: number, agora: Date = new Date()): string {
  return camposDataRetorno(new Date(agora.getTime() + dias * 86_400_000).toISOString()).data;
}

/**
 * Validação de tela (o banco confere de novo e recusa data passada).
 * Motivo: retorno no passado não dispara nada; mais de 1 ano costuma ser erro de digitação.
 */
export function validarDataRetorno(iso: string | null, agora: Date = new Date()): string | null {
  if (!iso) return 'Escolha a data e o horário do retorno.';
  const instante = new Date(iso).getTime();
  if (instante <= agora.getTime()) return 'A data do retorno precisa ser no futuro.';
  if (instante > agora.getTime() + LIMITE_DIAS_RETORNO * 86_400_000) return 'Escolha uma data em até 1 ano.';
  return null;
}
